"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var react_1 = require("react");
var shallowEquals = require("shallow-equal/objects");
var hooks_1 = require("./utils/hooks");
var XhrRequest_1 = require("./utils/XhrRequest");
var cache_1 = require("./utils/cache");
var RequestProvider_1 = require("./RequestProvider");
function hasRequestDepChanged(depsA, depsB) {
    // if deps are RequestParams
    if (depsA.url && depsB.url) {
        return XhrRequest_1.areRequestParamsEquals(depsA, depsB);
    }
    // if deps are contextHeader
    return shallowEquals(depsA, depsB);
}
function getRequestParams(options, contextHeaders) {
    var url = options.url, method = options.method, headers = options.headers, query = options.query, body = options.body, responseType = options.responseType;
    return {
        url: url,
        method: method,
        headers: tslib_1.__assign({}, contextHeaders, headers),
        query: query,
        body: body,
        responseType: responseType,
    };
}
function getRequestOptions(options) {
    var loaderDelay = options.loaderDelay, withProgress = options.withProgress, _a = options.abortOnUnmount, abortOnUnmount = _a === void 0 ? true : _a;
    return {
        loaderDelay: loaderDelay,
        withProgress: withProgress,
        abortOnUnmount: abortOnUnmount,
    };
}
function getRequest(options, contextHeaders) {
    var _a = options.fetchPolicy, fetchPolicy = _a === void 0 ? 'cache-first' : _a;
    var requestParams = getRequestParams(options, contextHeaders);
    var requestOptions = getRequestOptions(options);
    var cachedRequest = cache_1.default.getRequest(requestParams);
    if (cachedRequest) {
        cachedRequest.updateOptions(requestOptions);
        if (fetchPolicy === 'cache-first' || cachedRequest.state.status === 'LOADING') {
            return cachedRequest;
        }
    }
    var request = new XhrRequest_1.default(requestParams, requestOptions);
    cache_1.default.addRequest(request);
    return request;
}
function useFetch(options) {
    var contextHeaders = react_1.useContext(RequestProvider_1.RequestContext);
    var requestParams = getRequestParams(options, contextHeaders);
    var request = hooks_1.useSpecificMemo(function () { return getRequest(options, contextHeaders); }, [requestParams], hasRequestDepChanged);
    var _a = react_1.useState(request.state), fetchState = _a[0], setFetchState = _a[1];
    react_1.useEffect(function () {
        // don't send request when `preventRequest` is enabled
        if (options.preventRequest) {
            setFetchState(tslib_1.__assign({}, fetchState, { status: 'NOT_SEND', withLoader: false, progress: { loaded: 0, total: 1 }, statusCode: 0 }));
            return;
        }
        // set request state when request changed
        setFetchState(request.state);
        var updateRequestState = function (requestState) {
            var onSuccess = options.onSuccess, onError = options.onError;
            if (onSuccess && requestState.status === 'SUCCESS') {
                onSuccess(requestState.data, request.params);
            }
            else if (onError && requestState.status === 'FAILED') {
                onError(requestState.error, requestState.statusCode, request.params);
            }
            setFetchState(requestState);
        };
        request.addStateListener(updateRequestState);
        switch (request.state.status) {
            case 'FAILED':
                request.refetch();
                break;
            case 'NOT_SEND':
                request.fetch();
                break;
        }
        return function () {
            var onSuccess = options.onSuccess, onError = options.onError, _a = options.abortOnUnmount, abortOnUnmount = _a === void 0 ? true : _a;
            if (request.state.status === 'LOADING' && !abortOnUnmount) {
                if (onSuccess) {
                    request.addSuccessCallbacks(onSuccess);
                }
                if (onError) {
                    request.addErrorCallbacks(onError);
                }
            }
            request.removeStateListener(updateRequestState);
        };
    }, [request, options.preventRequest]);
    return tslib_1.__assign({}, fetchState, { refetch: request.refetch });
}
exports.default = useFetch;
